import React from 'react';
import { ArrowLeft, DollarSign, Brain, Shield, Database, Cloud, Server } from 'lucide-react';
import './CostReport.css';

interface CostReportProps {
    onBack: () => void;
}

interface CostItem {
    service: string;
    description: string;
    monthlyCost: string;
    category: 'ai' | 'infrastructure' | 'security' | 'overhead';
    icon: React.ReactNode;
}

const costData: CostItem[] = [
    // AI
    { service: 'Amazon Bedrock (Claude)', description: 'Chat, event analysis, SMS parsing, daily summaries', monthlyCost: '~$0.50', category: 'ai', icon: <Brain size={16} /> },
    // Infrastructure
    { service: 'AWS Lambda', description: 'Serverless compute for all API functions', monthlyCost: '<$0.01', category: 'infrastructure', icon: <Server size={16} /> },
    { service: 'DynamoDB', description: 'Health event storage, user context, knowledge base', monthlyCost: '<$0.01', category: 'infrastructure', icon: <Database size={16} /> },
    { service: 'API Gateway', description: 'HTTPS endpoints for the web app', monthlyCost: '<$0.01', category: 'infrastructure', icon: <Cloud size={16} /> },
    { service: 'S3 + CloudFront', description: 'Static site hosting + CDN', monthlyCost: '<$0.01', category: 'infrastructure', icon: <Cloud size={16} /> },
    { service: 'Route 53', description: 'DNS for halleffect.click domains', monthlyCost: '~$1.50', category: 'infrastructure', icon: <Cloud size={16} /> },
    { service: 'Cognito', description: 'User authentication', monthlyCost: '<$0.01', category: 'infrastructure', icon: <Shield size={16} /> },
    // Security / account overhead
    { service: 'CloudTrail', description: 'API audit logging (account-wide)', monthlyCost: '~$8.00', category: 'overhead', icon: <Shield size={16} /> },
    { service: 'Systems Manager', description: 'Parameter Store, patch baselines', monthlyCost: '~$14.00', category: 'overhead', icon: <Server size={16} /> },
    { service: 'Security Lake', description: 'Centralized security data lake', monthlyCost: '~$2.00', category: 'overhead', icon: <Shield size={16} /> },
    { service: 'AWS Config', description: 'Resource compliance tracking', monthlyCost: '~$1.00', category: 'overhead', icon: <Shield size={16} /> },
    { service: 'Secrets Manager', description: 'API keys, Twilio credentials', monthlyCost: '~$1.20', category: 'overhead', icon: <Shield size={16} /> },
    { service: 'KMS', description: 'Encryption key management', monthlyCost: '~$1.00', category: 'overhead', icon: <Shield size={16} /> },
];

export const CostReport: React.FC<CostReportProps> = ({ onBack }) => {
    const appCosts = costData.filter(c => c.category === 'ai' || c.category === 'infrastructure');
    const overheadCosts = costData.filter(c => c.category === 'overhead');

    return (
        <div className="cost-report">
            <div className="cost-header">
                <button className="back-button" onClick={onBack}>
                    <ArrowLeft size={16} /> Back
                </button>
                <h2><DollarSign size={20} /> Cost Report</h2>
            </div>

            <div className="cost-summary">
                <div className="cost-summary-card app-cost">
                    <span className="cost-label">App Cost</span>
                    <span className="cost-value">~$2/mo</span>
                    <span className="cost-note">What this health tracker actually costs to run</span>
                </div>
                <div className="cost-summary-card overhead-cost">
                    <span className="cost-label">Account Overhead</span>
                    <span className="cost-value">~$27/mo</span>
                    <span className="cost-note">AWS security & management services (account-wide)</span>
                </div>
            </div>

            <div className="cost-section">
                <h3>App Services</h3>
                <p className="section-desc">
                    The actual cost of running this health tracker is extremely low thanks to serverless architecture
                    and pay-per-use pricing. Most services are well within free tier or cost fractions of a penny.
                </p>
                <div className="cost-table">
                    {appCosts.map((item, i) => (
                        <div key={i} className="cost-row">
                            <div className="cost-row-icon">{item.icon}</div>
                            <div className="cost-row-info">
                                <strong>{item.service}</strong>
                                <span>{item.description}</span>
                            </div>
                            <div className="cost-row-amount">{item.monthlyCost}</div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="cost-section">
                <h3>Account Overhead</h3>
                <p className="section-desc">
                    These are AWS account-level services that aren't specific to this app — they'd exist even without the
                    health tracker. Consider disabling CloudTrail data events and Systems Manager if cost matters more than audit.
                </p>
                <div className="cost-table">
                    {overheadCosts.map((item, i) => (
                        <div key={i} className="cost-row overhead">
                            <div className="cost-row-icon">{item.icon}</div>
                            <div className="cost-row-info">
                                <strong>{item.service}</strong>
                                <span>{item.description}</span>
                            </div>
                            <div className="cost-row-amount">{item.monthlyCost}</div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="cost-tips">
                <h3>💡 Why So Cheap?</h3>
                <ul>
                    <li><strong>Serverless</strong> — Lambda, DynamoDB, and API Gateway only charge when you use them. No idle servers.</li>
                    <li><strong>Single user</strong> — Traffic is minimal; you'll never exceed free tiers for most services.</li>
                    <li><strong>Smart model selection</strong> — Haiku handles simple parsing (~$0.001/call), Sonnet handles chat and analysis.</li>
                    <li><strong>No always-on infra</strong> — No EC2, no RDS, no ECS. Everything scales to zero.</li>
                </ul>
            </div>
        </div>
    );
};
